import * as Sentry from '@sentry/nextjs';
import CTA from 'components/Campaign/CTA';
import { Petition } from 'components/cards';
import HexMap from 'components/HexMap/HexMap';
import Layout from 'components/layout';
import RangeSelector from 'components/RangeSelector';
import Anchor from 'components/typography/anchor';
import { useAnalytics } from 'hooks/useAnalytics';
import seedIcon from 'images/icons/sprout.svg';
import chartIcon from 'images/icons/trending-up.svg';
import petitionShareImg from 'images/shares/trending.png';
import { TIME_UNIT } from 'lib/api/themis/constants';
import {
  getPetitionCountsPerState,
  getRecentPetitions,
  getTrendingPetitionsV2,
} from 'lib/api/themis/queries';
import Head from 'next/head';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { DATA_TYPES } from 'siteconfig';
import { CampaignTypeList } from 'utilities/propTypes';

const REVALIDATE_TIME_MINUTES = 1;
const REVALIDATE_TIME_SECONDS = REVALIDATE_TIME_MINUTES * 60;

const PETITION_RANGES = [
  { id: 'daysSeven', label: '7 Days', icon: chartIcon },
  { id: 'daysThirty', label: '30 Days' },
  { id: 'monthsThree', label: '90 Days' },
  { id: 'latest', label: 'New', icon: seedIcon },
];

const Petitions = ({ trendingPetitions, recent, stateTotals }) => {
  const { trackEvent } = useAnalytics();
  const [selectedRangeId, setSelectedRangeId] = useState('daysSeven');
  const [searchQuery, setSearchQuery] = useState('');
  const { daysSeven, daysThirty, monthsThree, newPetitions } =
    processTrendingData({ trendingPetitions, recent });
  const petitionLayout = {
    daysSeven,
    daysThirty,
    monthsThree,
    latest: newPetitions,
  };

  // Filter selection change handler
  const handleRangeChange = (e) => {
    setSelectedRangeId(e.target.id);
  };

  // Search input change handler
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <Layout
      title="Petitions — Sign & Send Letters to Congress | Resistbot"
      description="Browse trending petitions and sign to send a letter to your elected officials in minutes. Join thousands of people making their voices heard."
      image={petitionShareImg}
      imageAltText='A stylized image of a woman with a megaphone shouting, over the words "we can do it"'
    >
      <Head>
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Resistbot Petitions RSS Feed"
          href="/petitions/feed.xml"
        />
        <link
          rel="alternate"
          type="application/atom+xml"
          title="Resistbot Petitions Atom Feed"
          href="/petitions/feed.atom"
        />
        <link
          rel="alternate"
          type="application/json"
          title="Resistbot Petitions JSON Feed"
          href="/petitions/feed.json"
        />
      </Head>
      <h1>Petitions</h1>
      <p className="mb-4 text-sm">
        Sign a petition, and Resistbot will send a copy instantly to your
        officials, or{' '}
        <Anchor
          className="anchor-text"
          href="/news/2020/01/14/introducing-petitions"
        >
          create your own
        </Anchor>{' '}
        in minutes! See{' '}
        <Anchor className="anchor-text" href="/letters">
          open letters
        </Anchor>{' '}
        for all published letters.
      </p>

      <search>
        <form
          className="flex flex-row gap-2 sm:flex-nowrap"
          method="get"
          action="https://www.google.com/search"
        >
          <input
            className="form-input flex-1"
            type="text"
            name="q"
            id="q"
            aria-label="Search Query"
            placeholder="Type a petition topic here"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <input type="hidden" name="sitesearch" value="resist.bot/petitions" />
          <input
            type="submit"
            value="Search"
            className="flex-0 bg-pink-600 px-4 py-2 text-white shadow-sm transition-colors hover:bg-pink-500 focus:outline-hidden dark:bg-amber-400 dark:text-black dark:hover:bg-amber-300"
            onClick={() => {
              trackEvent({ eventName: 'search:petitions', query: searchQuery });
            }}
          />
        </form>
      </search>

      <HexMap
        states={stateTotals.states}
        highestStateCount={stateTotals.highestTotal}
        basePath={DATA_TYPES.PETITION.basePath}
      />

      <RangeSelector
        ranges={PETITION_RANGES}
        selectedRangeId={selectedRangeId}
        handleRangeChange={handleRangeChange}
      />

      <div className="flex flex-col space-y-1">
        {petitionLayout[selectedRangeId]}
      </div>

      <div className="m-10 flex flex-col gap-2 rounded-sm bg-transparent text-center">
        <p>Organize. Be heard. Start your own campaign in&nbsp;minutes.</p>
        <CTA className="mx-auto" keyword="petition" copy="Start a Petition" />
      </div>
    </Layout>
  );
};

const processTrendingData = ({ trendingPetitions, recent }) => {
  const daysSeven = trendingPetitions.daysSeven.map((petition, idx) => (
    <Petition
      key={petition.petition_code}
      petition={petition}
      intervalLabel="7d"
      idx={idx}
    />
  ));
  const daysThirty = trendingPetitions.daysThirty.map((petition, idx) => (
    <Petition
      key={petition.petition_code}
      petition={petition}
      intervalLabel="30d"
      idx={idx}
    />
  ));
  const monthsThree = trendingPetitions.monthsThree.map((petition, idx) => (
    <Petition
      key={petition.petition_code}
      petition={petition}
      intervalLabel="90d"
      idx={idx}
    />
  ));

  const newPetitions = recent.map((petition, idx) => (
    <Petition
      key={petition.petition_code}
      petition={petition}
      intervalLabel="7d"
      idx={idx}
    />
  ));
  return { daysSeven, daysThirty, monthsThree, newPetitions };
};

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(async () => {
  // Could merge these into a single query with three elements
  const recentPetitionsPromise = Promise.all([
    getPetitionCountsPerState(),
    getTrendingPetitionsV2({
      range: 'SEVEN_DAY',
      limit: 30,
    }),
    getTrendingPetitionsV2({
      range: 'THIRTY_DAY',
      limit: 30,
    }),
    getTrendingPetitionsV2({
      range: 'NINETY_DAY',
      limit: 30,
    }),
  ]);

  const [stateTotals, daysSeven, daysThirty, monthsThree] =
    await recentPetitionsPromise;

  return {
    props: {
      trendingPetitions: {
        daysSeven,
        daysThirty,
        monthsThree,
      },
      stateTotals,
      recent: await getRecentPetitions({
        since: 7,
        period: TIME_UNIT.DAY,
        limit: 30,
      }),
    },
    revalidate: REVALIDATE_TIME_SECONDS,
  };
});

Petitions.propTypes = {
  trendingPetitions: PropTypes.shape({
    daysSeven: PropTypes.arrayOf(CampaignTypeList),
    daysThirty: PropTypes.arrayOf(CampaignTypeList),
    monthsThree: PropTypes.arrayOf(CampaignTypeList),
  }),
  recent: PropTypes.arrayOf(CampaignTypeList),
  stateTotals: PropTypes.shape({
    states: PropTypes.shape({
      [PropTypes.string]: PropTypes.number,
    }),
    highestTotal: PropTypes.number,
  }),
};

export default Petitions;
